import { StripeAchPaymentSourceModel } from '../../../api/billing-types';
import { Control, Controller, FieldErrors } from 'react-hook-form';

import { states } from '../../../shared/stripe-address-utils';

import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

interface BankAccountPaymentMethodFormProps {
  control: Control<Partial<StripeAchPaymentSourceModel>>;
  errors: FieldErrors<Partial<StripeAchPaymentSourceModel>>;
}

export const BankAccountPaymentMethodForm = ({
  control,
  errors,
}: BankAccountPaymentMethodFormProps) => {
  return (
    <Box>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid
          size={{ xs: 12, md: 12 }}
          sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
        >
          <Controller
            name="account_holder_name"
            rules={{
              required: `Please provide an account holder name.`,
            }}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name on Account"
                error={!!errors.account_holder_name}
                helperText={
                  errors.account_holder_name?.message ||
                  'Enter the name of the company if a corporate account or the name of the individual if an individual account.'
                }
                size="small"
                fullWidth
                required
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 12 }}
          sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
        >
          <Controller
            name="account_holder_type"
            rules={{
              required: `Please provide a bank account type.`,
            }}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Account Holder Type"
                error={!!errors.account_holder_type}
                helperText={errors.account_holder_type?.message || ' '}
                size="small"
                fullWidth
                required
                select
              >
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="company">Company</MenuItem>
              </TextField>
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 12 }}
          sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
        >
          <Controller
            name="routing_number"
            rules={{
              required: `Please provide a routing number.`,
              pattern: {
                value: /^\d{9}$/,
                message: 'Routing number must be exactly 9 digits',
              },
              validate: {
                checksum: (value) => {
                  if (!value || value.length !== 9) return true; // Let pattern validation handle this
                  // Routing number checksum validation
                  const digits = value.split('').map(Number);
                  const sum =
                    3 * (digits[0] + digits[3] + digits[6]) +
                    7 * (digits[1] + digits[4] + digits[7]) +
                    (digits[2] + digits[5] + digits[8]);
                  return sum % 10 === 0 || 'Invalid routing number';
                },
              },
            }}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Routing Number"
                error={!!errors.routing_number}
                helperText={errors.routing_number?.message || ' '}
                size="small"
                fullWidth
                required
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 12 }}
          sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
        >
          <Controller
            name="account_number"
            rules={{
              required: `Please provide an account number.`,
              pattern: {
                value: /^\d+$/,
                message: 'Account number must contain only numbers',
              },
              minLength: {
                value: 8,
                message: 'Account number must be at least 8 digits',
              },
              maxLength: {
                value: 17,
                message: 'Account number cannot exceed 17 digits',
              },
            }}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Account Number"
                error={!!errors.account_number}
                helperText={errors.account_number?.message || ' '}
                size="small"
                fullWidth
                required
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 12 }}
          sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
        >
          <Controller
            name={'address_line1'}
            control={control}
            rules={{
              required: `Please provide a street address.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'address',
                }}
                InputLabelProps={{
                  htmlFor: 'address',
                }}
                label="Street Address"
                fullWidth
                required
                autoComplete="address-line1"
                error={!!errors.address_line1}
                helperText={
                  errors.address_line1 ? errors.address_line1.message : ' '
                }
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name={'address_city'}
            control={control}
            rules={{
              required: `Please provide a city.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'city',
                }}
                InputLabelProps={{
                  htmlFor: 'city',
                }}
                label="City"
                fullWidth
                required
                autoComplete="address-level2"
                error={!!errors.address_city}
                helperText={
                  errors.address_city ? errors.address_city.message : ' '
                }
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name="address_state"
            control={control}
            rules={{
              required: `Please provide a state.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'state',
                }}
                InputLabelProps={{
                  htmlFor: 'state',
                }}
                label="State"
                fullWidth
                required
                autoComplete="address-level1"
                select
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 450,
                      },
                    },
                  },
                }}
                error={!!errors.address_state}
                helperText={
                  errors.address_state ? errors.address_state.message : ' '
                }
              >
                {states.map((state) => (
                  <MenuItem key={state.value} value={state.value}>
                    {state.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 12 }}
          sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}
        >
          <Controller
            name={'metadata.nickName'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'nickName',
                }}
                InputLabelProps={{
                  htmlFor: 'nickName',
                }}
                label="Nick Name"
                fullWidth
              />
            )}
          />
        </Grid>
      </Grid>

      <Typography variant="caption">
        By continuing, you authorize Promevo to credit and debit your bank
        account electronically, including debits to fund any negative balance
        that may arise. By clicking add, you agree to Promevo's{' '}
        <Link
          href="http://www.promevo.com/policies/promevoprivacypolicy.html"
          target="_blank"
        >
          privacy policy
        </Link>
        .
      </Typography>
    </Box>
  );
};
